import { plural } from '../../shared/format/plural';
import type { PersonalSignalContext } from './personalSignalContext';
import { getBranchBenchmarkTicket } from './personalSignalContext';
import type {
  PersonalDashboardFacts,
  PersonalOperatorFacts,
  PersonalSignal,
  PersonalSignalSeverity,
} from './reportsPersonalTypes';
import { formatMoney } from '../../shared/format/public-api';

const HIGH_CUSTOM_PCT = 0.25;
const LOW_TICKET_RATIO = 0.55;
const HIGH_PENDING_COUNT = 3;
const CASH_GAP_MIN_BANDS = 2;
const TICKET_30D_MIN_SALES = 5;
const TICKET_30D_DROP_RATIO = 0.7;
const STOCK_NO_SALES_MIN_DAYS = 1;
const STOCK_NO_SALES_MIN_DAYS_LONG = 2;
const SHRINKAGE_SALES_RATIO = 0.03;
const SHRINKAGE_MIN_TOTAL = 5000;
const HIGH_VOIDED_MIN_COUNT = 2;
const HIGH_VOIDED_COUNT = 3;
const HIGH_VOIDED_SALES_RATIO = 0.05;
const HIGH_CUSTOMER_NET_MIN = 10_000;

function branchSignal(
  code: string,
  severity: PersonalSignalSeverity,
  title: string,
  detail: string,
  metrics?: Record<string, string | number>
): PersonalSignal {
  return { code, severity, title, detail, operatorUserId: null, metrics };
}

function operatorSignal(
  op: PersonalOperatorFacts,
  code: string,
  severity: PersonalSignalSeverity,
  title: string,
  detail: string,
  metrics?: Record<string, string | number>
): PersonalSignal {
  return {
    code,
    severity,
    title,
    detail,
    operatorUserId: op.cashierUserId,
    metrics,
  };
}

export function evaluateVoidedSalesBranch(facts: PersonalDashboardFacts): PersonalSignal[] {
  const count = facts.branch.voidedSalesCount ?? 0;
  if (count <= 0) return [];
  return [
    branchSignal(
      'voided_sales',
      'info',
      'Ventas anuladas',
      `${count} ${plural(count, 'venta anulada', 'ventas anuladas')} en el período.`,
      { count, total: facts.branch.voidedSalesTotal ?? 0 }
    ),
  ];
}

export function evaluateNoCashierBranch(facts: PersonalDashboardFacts): PersonalSignal[] {
  if (facts.branch.orphanSalesCount <= 0) return [];
  return [
    branchSignal(
      'no_cashier',
      'review',
      'Ventas sin operador',
      `${facts.branch.orphanSalesCount} ${plural(facts.branch.orphanSalesCount, 'venta', 'ventas')} sin usuario identificado en el período.`,
      {
        count: facts.branch.orphanSalesCount,
        total: facts.branch.orphanSalesTotal,
      }
    ),
  ];
}

export function evaluateHighCustomItemsBranch(facts: PersonalDashboardFacts): PersonalSignal[] {
  if (facts.branch.salesCount < 5) return [];
  if (facts.branch.customLinePct < HIGH_CUSTOM_PCT) return [];
  return [
    branchSignal(
      'high_custom_items',
      'review',
      'Muchos ítems fuera de catálogo',
      `${(facts.branch.customLinePct * 100).toFixed(0)}% de las líneas son sueltas o «Varios» (promedio de la sucursal).`,
      { pct: facts.branch.customLinePct }
    ),
  ];
}

export function evaluateHighCustomItemsOperator(
  op: PersonalOperatorFacts,
  _facts: PersonalDashboardFacts,
  ctx: PersonalSignalContext
): PersonalSignal[] {
  if (op.salesCount < 3) return [];
  if (op.customLinePct < Math.max(HIGH_CUSTOM_PCT, ctx.branchCustomLinePct * 1.35)) return [];
  return [
    operatorSignal(
      op,
      'high_custom_items',
      'review',
      'Muchos ítems sueltos',
      `${(op.customLinePct * 100).toFixed(0)}% de sus líneas son sueltas o «Varios».`,
      { pct: op.customLinePct }
    ),
  ];
}

export function evaluateCashShortageOperator(op: PersonalOperatorFacts): PersonalSignal[] {
  if (op.cashClosedSessions <= 0) return [];
  if (op.cashDifferenceTotal >= 0 && op.cashShortageSessions === 0) return [];
  const shortage = op.cashDifferenceTotal < 0 ? Math.abs(op.cashDifferenceTotal) : 0;
  if (shortage <= 0 && op.cashShortageSessions <= 0) return [];
  return [
    operatorSignal(
      op,
      'cash_shortage',
      op.cashShortageSessions >= 2 ? 'priority' : 'review',
      'Faltante en cierre de caja',
      op.cashShortageSessions > 0
        ? `${op.cashShortageSessions} ${plural(op.cashShortageSessions, 'cierre', 'cierres')} con faltante en el período.`
        : `Diferencia acumulada en cierres: ${formatMoney(shortage)}.`,
      {
        difference: op.cashDifferenceTotal,
        sessions: op.cashShortageSessions,
      }
    ),
  ];
}

export function evaluateCashierMismatchOperator(op: PersonalOperatorFacts): PersonalSignal[] {
  if (op.cashierMismatchCount <= 0) return [];
  return [
    operatorSignal(
      op,
      'cashier_mismatch',
      'review',
      'Ventas en turno ajeno',
      `${op.cashierMismatchCount} ${plural(op.cashierMismatchCount, 'venta', 'ventas')} en sesión abierta por otro operador.`,
      { count: op.cashierMismatchCount }
    ),
  ];
}

export function evaluateLowTicketVsTeamOperator(
  op: PersonalOperatorFacts,
  facts: PersonalDashboardFacts
): PersonalSignal[] {
  if (op.salesCount < 5) return [];
  const bench = getBranchBenchmarkTicket(facts.operators);
  if (bench <= 0) return [];
  if (op.ticketAverage >= bench * LOW_TICKET_RATIO) return [];
  return [
    operatorSignal(
      op,
      'low_ticket_vs_team',
      'review',
      'Ticket bajo vs equipo',
      `Ticket promedio ${formatMoney(Math.round(op.ticketAverage))} vs ${formatMoney(Math.round(bench))} del equipo.`,
      { ticket: op.ticketAverage, bench }
    ),
  ];
}

export function evaluateCashGapOperator(op: PersonalOperatorFacts): PersonalSignal[] {
  if (op.cashGapBandCount < CASH_GAP_MIN_BANDS) return [];
  const detail =
    op.cashGapExampleDetail?.trim() ||
    `${op.cashGapBandCount} ${plural(op.cashGapBandCount, 'franja horaria', 'franjas horarias')} sin ventas con caja abierta mientras la sucursal vendió.`;
  return [
    operatorSignal(
      op,
      'cash_gap',
      'review',
      'Hueco con caja abierta',
      detail,
      { bands: op.cashGapBandCount }
    ),
  ];
}

export function evaluatePendingPaymentsOperator(
  op: PersonalOperatorFacts,
  facts: PersonalDashboardFacts
): PersonalSignal[] {
  if (op.pendingPayments < HIGH_PENDING_COUNT) return [];
  const peers = facts.operators.filter((p) => p.cashierUserId && p.cashierUserId !== op.cashierUserId);
  const peerAvg =
    peers.length > 0
      ? peers.reduce((s, p) => s + p.pendingPayments, 0) / peers.length
      : 0;
  if (peerAvg > 0 && op.pendingPayments < peerAvg * 1.5) return [];
  return [
    operatorSignal(
      op,
      'pending_payments',
      'review',
      'Pagos pendientes altos',
      `${op.pendingPayments} ${plural(op.pendingPayments, 'venta', 'ventas')} con saldo pendiente en el período.`,
      { count: op.pendingPayments }
    ),
  ];
}

export function evaluateTicketBelowOwnHistoryOperator(op: PersonalOperatorFacts): PersonalSignal[] {
  if (op.salesCount < TICKET_30D_MIN_SALES) return [];
  if (op.ticketAvg30d <= 0) return [];
  if (op.ticketAverage >= op.ticketAvg30d * TICKET_30D_DROP_RATIO) return [];
  return [
    operatorSignal(
      op,
      'ticket_below_own_history',
      'review',
      'Ticket bajo vs tu habitual',
      `Ticket promedio ${formatMoney(Math.round(op.ticketAverage))} vs ${formatMoney(Math.round(op.ticketAvg30d))} de los últimos 30 días.`,
      { ticket: op.ticketAverage, bench30d: op.ticketAvg30d }
    ),
  ];
}

export function evaluateStockNoSalesOperator(
  op: PersonalOperatorFacts,
  facts: PersonalDashboardFacts
): PersonalSignal[] {
  const minDays =
    facts.periodDays > 7 ? STOCK_NO_SALES_MIN_DAYS_LONG : STOCK_NO_SALES_MIN_DAYS;
  if (op.stockNoSalesDays < minDays) return [];
  return [
    operatorSignal(
      op,
      'stock_no_sales',
      'review',
      'Stock sin ventas el mismo día',
      `${op.stockNoSalesDays} ${plural(op.stockNoSalesDays, 'día', 'días')} con movimientos de stock y sin ventas registradas.`,
      { days: op.stockNoSalesDays }
    ),
  ];
}

export function evaluateHighShrinkageOperator(op: PersonalOperatorFacts): PersonalSignal[] {
  if (op.salesTotal <= 0) return [];
  if (op.shrinkageTotal < SHRINKAGE_MIN_TOTAL) return [];
  const ratio = op.shrinkageTotal / op.salesTotal;
  if (ratio < SHRINKAGE_SALES_RATIO) return [];
  return [
    operatorSignal(
      op,
      'high_shrinkage',
      'review',
      'Merma alta vs ventas',
      `Merma ${formatMoney(op.shrinkageTotal)} (${(ratio * 100).toFixed(1)}% de su facturación).`,
      { shrinkage: op.shrinkageTotal, ratio }
    ),
  ];
}

export function evaluateHighCustomerBalanceOperator(
  op: PersonalOperatorFacts,
  facts: PersonalDashboardFacts
): PersonalSignal[] {
  const net = op.customerChargesTotal - op.customerPaymentsTotal;
  if (net < HIGH_CUSTOMER_NET_MIN) return [];
  const peers = facts.operators.filter((p) => p.cashierUserId && p.cashierUserId !== op.cashierUserId);
  const peerNetAvg =
    peers.length > 0
      ? peers.reduce((s, p) => s + (p.customerChargesTotal - p.customerPaymentsTotal), 0) / peers.length
      : 0;
  if (peerNetAvg > 0 && net < peerNetAvg * 1.5) return [];
  return [
    operatorSignal(
      op,
      'high_fiado_balance',
      'review',
      'Crédito cargado sin cobrar',
      `Neto customer ${formatMoney(net)} en el período (cargos − cobros).`,
      { net }
    ),
  ];
}

export function evaluateHighVoidedSalesOperator(
  op: PersonalOperatorFacts,
  facts: PersonalDashboardFacts,
  ctx: PersonalSignalContext
): PersonalSignal[] {
  if (op.voidedCount < HIGH_VOIDED_MIN_COUNT) return [];
  const ratio = op.salesTotal > 0 ? op.voidedTotal / op.salesTotal : 1;
  const countHigh = op.voidedCount >= HIGH_VOIDED_COUNT;
  const ratioHigh = ratio > HIGH_VOIDED_SALES_RATIO && op.voidedCount >= HIGH_VOIDED_MIN_COUNT;
  let peerHigh = false;
  if (ctx.teamMode !== 'solo') {
    const peers = facts.operators.filter((p) => p.cashierUserId && p.cashierUserId !== op.cashierUserId);
    const peerVoidAvg =
      peers.length > 0 ? peers.reduce((s, p) => s + p.voidedCount, 0) / peers.length : 0;
    peerHigh = peerVoidAvg > 0 && op.voidedCount > peerVoidAvg * 1.5;
  }
  if (!countHigh && !ratioHigh && !peerHigh) return [];
  return [
    operatorSignal(
      op,
      'high_voided_sales',
      op.voidedCount >= HIGH_VOIDED_COUNT ? 'priority' : 'review',
      'Muchas ventas anuladas',
      `Anuló ${op.voidedCount} ${plural(op.voidedCount, 'venta', 'ventas')} por ${formatMoney(op.voidedTotal)} en el período.`,
      { count: op.voidedCount, total: op.voidedTotal }
    ),
  ];
}
